import { COVERAGE_CATALOG, COVERAGE_LEVELS, coverageIncluded } from '../models/coverage';
import { Capability, Company, HiddenProduct, ImportRun, LineOfBusiness, Policy, TreeMember } from '../models/domain.models';
import { LINE_LABELS } from '../models/labels';
import {
  CatalogLineGroup, CatalogPlan, CatalogProduct, CatalogView, CompanyCapabilityRow, CompanyCard, ImportCompany, ImportRunRow,
} from '../models/views.models';
import { PRODUCT_TIER } from './catalog-data';
import { hashOf, subtreeOids } from './commission-math';
import { failure, Outcome } from './outcome';
import { initialVersion } from './policy-helpers';
import { importRun } from './seed-portfolio';
import { StoreHost } from './store-host';
import { TreeEngine } from './tree-engine';

export interface StandalonePolicyBody {
  personOid: string;
  companyOid: string;
  lineOfBusiness: LineOfBusiness;
  number: string;
  startDate: string;
  endDate: string;
  premium: number;
  product?: string;
  plan?: string;
  subject?: string;
}

export interface ImportBody {
  companyOid: string;
  fileName?: string;
}

const CAPABILITY_TEXT: Record<Capability, Partial<Record<'api' | 'manual' | 'file', string>>> = {
  quote: { api: 'Cotiza online' },
  issue: { api: 'Emite online', manual: 'Emisión: se hace en el sitio de la compañía y se registra a mano' },
  endorse: { api: 'Endosos: online', manual: 'Endosos: se registran a mano' },
  reportClaim: { api: 'Denuncia de siniestros: online', manual: 'Denuncia de siniestros: se hace en la compañía y se registra a mano' },
  queryClaims: { api: 'Seguimiento de siniestros: online', manual: 'Seguimiento de siniestros: se actualiza a mano' },
  queryPolicies: { api: 'Consulta de pólizas: online', manual: 'Pólizas: se cargan a mano' },
  documents: { api: 'Documentos: se traen de la compañía', manual: 'Documentos: se suben a mano' },
  collections: { api: 'Cobranza: online', file: 'Cobranza: por archivo', manual: 'Cobranza: se carga a mano' },
  settlements: { api: 'Liquidación de comisión: online', file: 'Liquidación de comisión: por archivo', manual: 'Liquidación de comisión: se carga a mano' },
};

const SUMMARY_PARTS: Capability[] = ['endorse', 'reportClaim', 'queryClaims'];

function versionOf(key: string): string {
  const h = hashOf(key);
  return `v${1 + (h % 3)}.${(h >> 3) % 10}`;
}

export function productKey(companyOid: string, line: LineOfBusiness, name: string): string {
  return `${companyOid}|${line}|${name}`;
}

/** P-84 companies, P-85 catalog, P-46 import history and P-43 manual registration (D-031-fe). */
export class PortfolioEngine {
  constructor(private readonly host: StoreHost, private readonly tree: TreeEngine) {}

  private get db() {
    return this.host.db;
  }

  private me(): TreeMember | null {
    const oid = this.host.user()?.memberOid;
    return this.db.members.find(m => m.oid === oid) ?? null;
  }

  private holder(): TreeMember | null {
    const me = this.me();
    if (!me) return null;
    return me.kind === 'producer' ? me : this.db.members.find(m => m.oid === me.parentOid) ?? me;
  }

  private treeOid(): string | null {
    return this.host.user()?.treeOid ?? null;
  }

  private isProducer(): boolean {
    return this.host.user()?.role === 'producer';
  }

  // ── P-84 ──────────────────────────────────────────────────────

  companiesView(): CompanyCard[] {
    const holder = this.holder();
    if (!holder) return [];
    return this.db.companies.map(company => {
      const checks = company.lines.map(line => ({ line, ...this.tree.availability(holder.oid, company.oid, line) }));
      const enabled = checks.some(c => c.ok);
      return {
        oid: company.oid, name: company.name, enabled,
        disabledReason: enabled ? null : checks[0]?.reason ?? null,
        lines: checks.map(c => ({ line: c.line, enabled: c.ok })),
        capabilities: (Object.keys(CAPABILITY_TEXT) as Capability[]).map((capability): CompanyCapabilityRow => ({
          capability, mode: company.capabilities[capability],
          text: CAPABILITY_TEXT[capability][company.capabilities[capability]] ?? '',
        })),
        summary: this.summary(company),
        simulatedBehaviour: company.simulatedBehaviour,
      };
    });
  }

  private summary(company: Company): string {
    const c = company.capabilities;
    const head = c.quote === 'api' && c.issue === 'api' ? 'Cotiza y emite online'
      : c.quote === 'api' ? 'Cotiza online, emite en el sitio de la compañía' : 'Se opera a mano';
    const parts = SUMMARY_PARTS.map(key => CAPABILITY_TEXT[key][c[key]] ?? '');
    return [head, ...parts.filter(Boolean)].join(' · ');
  }

  // ── P-85 ──────────────────────────────────────────────────────

  catalogView(): CatalogView {
    const treeOid = this.treeOid();
    const hidden = new Set(
      this.db.hiddenProducts.filter(h => h.treeOid === treeOid).map(h => productKey(h.companyOid, h.lineOfBusiness, h.product)),
    );
    const lines = Object.keys(LINE_LABELS) as LineOfBusiness[];
    const groups: CatalogLineGroup[] = lines.map(line => ({
      line,
      products: this.db.companies.filter(c => c.lines.includes(line)).map((company): CatalogProduct => {
        const name = `${LINE_LABELS[line]} ${PRODUCT_TIER[company.oid] ?? 'Base'}`;
        const key = productKey(company.oid, line, name);
        const plans = this.plansOf(company, line, name);
        return {
          key, companyOid: company.oid, companyName: company.name, name, plans, hidden: hidden.has(key),
          discontinued: plans.length > 0 && plans.every(p => p.discontinued),
        };
      }),
    }));
    return { groups };
  }

  private plansOf(company: Company, line: LineOfBusiness, product: string): CatalogPlan[] {
    const tier = PRODUCT_TIER[company.oid] ?? 'Base';
    const reviews = this.db.catalogReviews.filter(r => r.companyOid === company.oid && r.lineOfBusiness === line && r.product === product);
    const plan = (name: string, level: string, discontinued: boolean): CatalogPlan => ({
      name, level, discontinued, version: versionOf(`${company.oid}|${name}`),
      coverages: COVERAGE_CATALOG[line].filter(def => coverageIncluded(line, level, def)).map(def => def.name),
    });
    const base = COVERAGE_LEVELS[line].map(level => {
      const name = `${tier} ${level}`;
      return plan(name, level, reviews.some(r => r.planName === name && r.state === 'discontinued'));
    });
    const known = new Set(base.map(p => p.name));
    const extra = reviews.filter(r => !known.has(r.planName) && r.state !== 'pending')
      .map(r => plan(r.planName, r.confirmedLevel ?? r.suggestedLevel, r.state === 'discontinued'));
    return [...base, ...extra];
  }

  setProductHidden(key: string, hide: boolean): Outcome<CatalogView> {
    const treeOid = this.treeOid();
    if (!treeOid || !this.isProducer()) return failure(403, 'FORBIDDEN', 'Solo el productor oculta productos de su árbol');
    const product = this.catalogView().groups
      .flatMap(g => g.products.map(p => ({ ...p, line: g.line })))
      .find(p => p.key === key);
    if (!product) return failure(404, 'NOT_FOUND', 'Producto inexistente');
    const same = (h: HiddenProduct) => h.treeOid === treeOid && productKey(h.companyOid, h.lineOfBusiness, h.product) === key;
    const rest = this.db.hiddenProducts.filter(h => !same(h));
    this.db.hiddenProducts.length = 0;
    this.db.hiddenProducts.push(...rest);
    if (hide) this.db.hiddenProducts.push({ treeOid, companyOid: product.companyOid, lineOfBusiness: product.line, product: product.name });
    return { value: this.catalogView() };
  }

  // ── P-46 ──────────────────────────────────────────────────────

  importCompanies(): ImportCompany[] {
    return this.db.companies.map(c => ({
      oid: c.oid, name: c.name, via: c.capabilities.queryPolicies === 'api' ? 'api' : 'file', lines: c.lines,
    }));
  }

  private runRow(run: ImportRun): ImportRunRow {
    return {
      ...run,
      companyName: this.db.companies.find(c => c.oid === run.companyOid)?.name ?? '—',
      memberName: this.db.members.find(m => m.oid === run.memberOid)?.name ?? '—',
    };
  }

  importRuns(): ImportRunRow[] {
    const me = this.me();
    if (!me || !this.isProducer()) return [];
    const owners = subtreeOids(this.db.members, me.oid);
    return this.db.importRuns
      .filter(r => r.treeOid === this.treeOid() && owners.has(r.memberOid))
      .sort((a, b) => b.date.localeCompare(a.date) || b.oid.localeCompare(a.oid))
      .map(r => this.runRow(r));
  }

  runImport(body: ImportBody): Outcome<ImportRunRow> {
    const me = this.me();
    const company = this.db.companies.find(c => c.oid === body.companyOid);
    if (!me || !this.isProducer() || !company) return failure(404, 'NOT_FOUND', 'Compañía inexistente');
    const via = company.capabilities.queryPolicies === 'api' ? 'api' : 'file';
    if (via === 'file' && !body.fileName?.trim()) {
      return failure(400, 'IMPORT_INVALID', 'Elegí el archivo con el formato de la plataforma');
    }
    const run = importRun(
      `imp-${String(this.host.nextSequence()).padStart(4, '0')}`, this.treeOid() ?? '', me.oid, this.host.today(), company, via,
      via === 'file' ? body.fileName?.trim() ?? null : null,
    );
    this.db.importRuns.unshift(run);
    return { value: this.runRow(run) };
  }

  undoImport(oid: string): Outcome<ImportRunRow> {
    const visible = this.importRuns().some(r => r.oid === oid);
    const run = visible ? this.db.importRuns.find(r => r.oid === oid) : undefined;
    if (!run) return failure(404, 'NOT_FOUND', 'Importación inexistente');
    if (run.undone) return failure(409, 'IMPORT_UNDONE', 'La importación ya estaba deshecha');
    run.undone = true;
    run.undoneDate = this.host.today();
    return { value: this.runRow(run) };
  }

  // ── P-43 ──────────────────────────────────────────────────────

  registerStandalonePolicy(body: StandalonePolicyBody): Outcome<Policy> {
    const me = this.me();
    const holder = this.holder();
    const company = this.db.companies.find(c => c.oid === body.companyOid);
    if (!me || !holder || !this.isProducer() || !company) return failure(404, 'NOT_FOUND', 'Compañía inexistente');
    const owners = subtreeOids(this.db.members, me.oid);
    const person = this.db.persons.find(p => p.oid === body.personOid && owners.has(p.ownerOid));
    if (!person) return failure(404, 'NOT_FOUND', 'Persona inexistente en tu árbol');
    if (!company.lines.includes(body.lineOfBusiness)) return failure(400, 'POLICY_INVALID', 'La compañía no opera ese ramo');
    const availability = this.tree.availability(holder.oid, company.oid, body.lineOfBusiness);
    if (!availability.ok) return failure(409, 'POLICY_COMPANY_UNAVAILABLE', `${company.name} no está habilitada: ${availability.reason}`);
    const number = body.number?.trim();
    if (!number || !(body.premium > 0) || !body.startDate || !body.endDate || body.endDate <= body.startDate) {
      return failure(400, 'POLICY_INVALID', 'Indicá número, premio y una vigencia con el fin posterior al inicio');
    }
    if (this.db.policies.some(p => p.companyOid === company.oid && p.number === number)) {
      return failure(409, 'POLICY_DUPLICATE', 'Ya existe una póliza con ese número en esa compañía');
    }
    const today = this.host.today();
    const subject = body.subject?.trim() || 'Riesgo pendiente de carga';
    const policy: Policy = {
      oid: `pol-${this.host.nextSequence()}`, number, lineOfBusiness: body.lineOfBusiness, companyOid: company.oid,
      personOid: person.oid, ownerOid: me.oid, origin: 'manual',
      status: body.endDate < today ? 'expired' : body.startDate > today ? 'pending' : 'active',
      startDate: body.startDate, endDate: body.endDate, premium: body.premium, subject,
      lastSyncDate: today, divergent: false, renewalOfOid: null, installmentsLoaded: false,
      licenseHolderOid: holder.oid, attributedToOid: me.oid,
      versions: [{ ...initialVersion(body.startDate, body.premium, subject), endDate: body.endDate }],
      product: body.product || undefined, plan: body.plan || undefined,
    };
    this.db.policies.unshift(policy);
    person.kind = 'client';
    this.host.notify(me.oid, 'issuance', 'Póliza registrada', `Se registró a mano la póliza ${policy.number}.`, 'policy', policy.oid);
    return { value: policy };
  }
}
